/**
 * ERP 디자인 토큰 (SSOT)
 * - 화면/컴포넌트는 hex를 직접 쓰지 말고 theme 또는 CSS 변수(--erp-*)를 사용
 * - 런타임 스킨: ErpThemeProvider 가 ThemeProvider + applyCssVariablesToDom 으로 동기화
 * - 글자 크기: rem (html --erp-font-size-root). Ctrl+휠과 달리 레이아웃 px는 유지
 */

import { FONT_SIZE_BASE_PX, fontFamilyStack, fontSizeTokensRem } from './erpFonts';

export const theme = {
  colors: {
    primary: '#2563EB',
    primaryDark: '#1D4ED8',
    primaryLight: '#DBEAFE',
    /** 컴팩트 호버 탭 목록에서 현재 열린 화면 배경 */
    tabHoverActive: '#DBEAFE',
    secondary: '#F0F4F8',
    tertiary: '#EFF6FF',
    quaternary: '#F5F7FA',
    quinary: '#E8ECF0',
    senary: '#D0D7DE',
    septenary: '#9CA3AF',
    octonary: '#6B7280',
    nonary: '#4B5563',
    denary: '#374151',
    white: '#FFFFFF',
    black: '#111827',
    navy: '#1F2937',
    red: '#EF4444',
    green: '#10B981',
    /** 트리 caret 등 */
    greenStrong: '#16A34A',
    blue: '#3B82F6',
    yellow: '#F59E0B',
    purple: '#8B5CF6',
    orange: '#F97316',
    pink: '#F472B6',
    brown: '#92400E',
    text: {
      primary: '#1F2937',
      secondary: '#4B5563',
      tertiary: '#9CA3AF',
      white: '#FFFFFF',
      link: '#2563EB',
    },
    status: {
      success: '#10B981',
      /** 입력가능 등 강조 성공 */
      successStrong: '#059669',
      error: '#EF4444',
      warning: '#F59E0B',
      /** 제출·적용일 미도래 등 */
      warningStrong: '#D97706',
      info: '#2563EB',
      dangerSoft: '#FEE2E2',
      dangerText: '#DC2626',
    },
    border: '#E5E7EB',
    borderLight: '#F0F0F0',
    /** 포커스 링 (다이얼로그 닫기 등) */
    focusRing: '#93C5FD',
    /** 사이드바 다크 로고 행 */
    sidebarDark: '#0F0F0F',
    background: {
      light: '#F8F9FA',
      medium: '#F3F4F6',
      dark: '#E5E7EB',
      white: '#FFFFFF',
      black: '#111827',
      default: '#F5F7FA',
      primary: '#2563EB',
      secondary: '#F8F9FA',
      tertiary: '#9CA3AF',
      quaternary: '#6B7280',
      quinary: '#D1D5DB',
      senary: '#F3F4F6',
      septenary: '#FAFAFA',
      input: '#FFFFFF',
      sidebar: '#F8F9FA',
      page: '#F5F7FA',
    },
    grid: {
      headerBg: '#F8F9FA',
      headerText: '#374151',
      headerAccent: '#2563EB',
      /** 활성 그리드 헤더 */
      headerActiveBg: '#2563EB',
      headerActiveFg: '#FFFFFF',
      headerActiveSort: '#FDE047',
      headerActiveHover: '#1E40AF',
      rowHover: '#F0F7FF',
      rowSelected: '#DBEAFE',
      cellSelected: 'rgba(37, 99, 235, 0.28)',
      cellFocused: '#2563EB',
      /** 블럭 선택 외곽 가이드 선 */
      cellBlockBorder: '#D97706',
      readonlyBg: '#F9FAFB',
      border: '#E8ECF0',
      emptyText: '#9CA3AF',
      /** 스택 셀 구분선 */
      stackDivider: '#CBD5E1',
    },
    /** 비활성 그리드 헤더(ActiveGrid) */
    gridInactive: {
      headerBg: '#F3F4F6',
      headerFg: '#111827',
    },
  },
  spacing: {
    xxxxs: '2px',
    xxxs: '4px',
    xxs: '6px',
    xs: '8px',
    sm: '12px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  borderRadius: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    pill: '999px',
    panel: '8px',
  },
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 2px 8px rgba(0, 0, 0, 0.06)',
    lg: '0 4px 16px rgba(0, 0, 0, 0.08)',
    card: '0 1px 3px rgba(0, 0, 0, 0.05)',
  },
  typography: {
    fontFamily: fontFamilyStack('pretendard'),
    gridFontFamily: fontFamilyStack('pretendard'),
    /** 본문 루트 px — html font-size. rem 토큰은 13px=1rem 비율 고정 */
    fontSizeRootPx: FONT_SIZE_BASE_PX,
    fontSize: fontSizeTokensRem(FONT_SIZE_BASE_PX),
  },
  layout: {
    sidebarWidth: '240px',
    sidebarCollapsedWidth: '56px',
    headerHeight: '48px',
    topChromeHeight: '40px',
    pagePadding: '12px',
    contentGap: '8px',
  },
  /** 컴포넌트 density — 화면별 재정의 금지 */
  components: {
    actionButton: {
      paddingY: '3px',
      paddingX: '8px',
      minHeight: '26px',
      fontSizeKey: 'sm',
      gap: '0.25rem',
    },
    form: {
      searchLabelWidth: '56px',
      fieldGap: '6px',
      rowGap: '12px',
    },
    treeToggle: {
      size: '12px',
      fontSize: '9px',
    },
  },
};

/** 조회 FormArea labelWidth — 화면별 '56px' 하드코딩 대신 사용 */
export const SEARCH_LABEL_W = theme.components.form.searchLabelWidth;

/**
 * :root CSS 변수 맵 (일괄 톤 변경·향후 스킨용)
 * @param {typeof theme} [t]
 * @returns {Record<string, string>}
 */
export function buildCssVariables(t = theme) {
  const btn = t.components.actionButton;
  const g = t.colors.grid;
  const form = t.components.form || {};
  const searchPanel = t.components.searchPanel || {};
  const sidebar = t.components.sidebar || {};
  const page = t.components.page || {};
  return {
    '--erp-color-primary': t.colors.primary,
    '--erp-color-primary-dark': t.colors.primaryDark,
    '--erp-color-primary-light': t.colors.primaryLight,
    '--erp-color-tab-hover-active': t.colors.tabHoverActive || t.colors.primaryLight,
    '--erp-color-border': t.colors.border,
    '--erp-color-border-light': t.colors.borderLight,
    '--erp-color-focus-ring': t.colors.focusRing,
    '--erp-color-sidebar-dark': t.colors.sidebarDark,
    '--erp-color-text': t.colors.text.primary,
    '--erp-color-text-secondary': t.colors.text.secondary,
    '--erp-color-text-tertiary': t.colors.text.tertiary,
    '--erp-color-text-link': t.colors.text.link,
    '--erp-color-bg-page': t.colors.background.page,
    '--erp-color-bg-white': t.colors.white,
    '--erp-color-bg-light': t.colors.background.light,
    '--erp-color-bg-medium': t.colors.background.medium,
    '--erp-color-bg-sidebar': t.colors.background.sidebar,
    '--erp-color-bg-toolbar': t.colors.background.septenary,
    '--erp-color-bg-input': t.colors.background.input,
    '--erp-color-on-accent': '#FFFFFF',
    '--erp-color-status-success': t.colors.status.success,
    '--erp-color-status-success-strong': t.colors.status.successStrong,
    '--erp-color-status-error': t.colors.status.error,
    '--erp-color-status-warning': t.colors.status.warning,
    '--erp-color-status-warning-strong': t.colors.status.warningStrong,
    '--erp-color-status-info': t.colors.status.info,
    '--erp-color-status-danger-text': t.colors.status.dangerText,
    '--erp-color-status-danger-soft': t.colors.status.dangerSoft,
    '--erp-color-green-strong': t.colors.greenStrong,
    '--erp-color-septenary': t.colors.septenary,
    '--erp-color-denary': t.colors.denary,
    '--erp-color-grid-header-bg': g.headerBg,
    '--erp-color-grid-header-text': g.headerText,
    '--erp-color-grid-header-accent': g.headerAccent,
    '--erp-color-grid-header-active-bg': g.headerActiveBg,
    '--erp-color-grid-header-active-fg': g.headerActiveFg,
    '--erp-color-grid-header-active-sort': g.headerActiveSort,
    '--erp-color-grid-header-active-hover': g.headerActiveHover,
    '--erp-color-grid-row-hover': g.rowHover,
    '--erp-color-grid-row-selected': g.rowSelected,
    '--erp-color-grid-cell-selected': g.cellSelected,
    '--erp-color-grid-cell-focused': g.cellFocused,
    '--erp-color-grid-cell-block-border': g.cellBlockBorder || g.cellFocused,
    '--erp-color-grid-border': g.border,
    '--erp-color-grid-readonly-bg': g.readonlyBg,
    '--erp-color-mode': t.meta?.colorMode || 'light',
    '--erp-font-family': t.typography.fontFamily,
    '--erp-grid-font-family': t.typography.gridFontFamily,
    '--erp-font-size-root': `${t.typography.fontSizeRootPx || FONT_SIZE_BASE_PX}px`,
    '--erp-font-size-xxs': t.typography.fontSize.xxs,
    '--erp-font-size-xs': t.typography.fontSize.xs,
    '--erp-font-size-sm': t.typography.fontSize.sm,
    '--erp-font-size-md': t.typography.fontSize.md,
    '--erp-font-size-lg': t.typography.fontSize.lg,
    '--erp-font-size-xl': t.typography.fontSize.xl,
    '--erp-font-size-xxl': t.typography.fontSize.xxl,
    '--erp-radius-xs': t.borderRadius.xs,
    '--erp-radius-sm': t.borderRadius.sm,
    '--erp-radius-md': t.borderRadius.md,
    '--erp-radius-lg': t.borderRadius.lg,
    '--erp-radius-panel': t.borderRadius.panel || t.borderRadius.md,
    '--erp-btn-padding-y': btn.paddingY,
    '--erp-btn-padding-x': btn.paddingX,
    '--erp-btn-min-height': btn.minHeight,
    '--erp-btn-font-size': t.typography.fontSize[btn.fontSizeKey] || t.typography.fontSize.sm,
    '--erp-btn-gap': btn.gap,
    '--erp-form-search-label-width': form.searchLabelWidth || t.components.form.searchLabelWidth,
    '--erp-form-field-gap': form.fieldGap || t.components.form.fieldGap,
    '--erp-form-row-gap': form.rowGap || t.components.form.rowGap,
    '--erp-control-height': form.controlHeight || '28px',
    '--erp-search-bar-min-height': searchPanel.barMinHeight || '28px',
    '--erp-search-bar-padding': searchPanel.barPadding || '2px 6px 2px 10px',
    '--erp-sidebar-width': t.layout.sidebarWidth,
    '--erp-sidebar-collapsed-width': t.layout.sidebarCollapsedWidth,
    '--erp-sidebar-item-height': sidebar.itemHeight || '32px',
    '--erp-sidebar-item-radius': sidebar.itemRadius || t.borderRadius.md,
    '--erp-page-padding': t.layout.pagePadding || t.spacing.sm,
    '--erp-content-gap': t.layout.contentGap || t.spacing.xs,
    '--erp-panel-shadow': page.panelShadow || 'none',
    '--erp-toolbar-shadow': page.toolbarShadow || 'none',
  };
}

/**
 * document.documentElement 에 CSS 변수 적용 (런타임 테마)
 * @param {typeof theme} [t]
 */
export function applyCssVariablesToDom(t = theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const vars = buildCssVariables(t);
  Object.entries(vars).forEach(([k, v]) => {
    root.style.setProperty(k, v);
  });
  root.setAttribute('data-erp-color-mode', t.meta?.colorMode || 'light');
  root.setAttribute('data-erp-ui-chrome', t.meta?.uiChromeType || 'type1');
  root.setAttribute('data-erp-font', t.meta?.fontFamilyId || 'pretendard');
  root.setAttribute('data-erp-font-size', String(t.typography.fontSizeRootPx || FONT_SIZE_BASE_PX));
}

/** styled-components용: var(--erp-…, fallback) */
export function cssVar(name, fallback) {
  const key = name.startsWith('--') ? name : `--erp-${name}`;
  if (fallback == null || fallback === '') return `var(${key})`;
  return `var(${key}, ${fallback})`;
}
